
import React, { useEffect, useState } from "react";
import { RxCounterClockwiseClock } from "react-icons/rx";
import DOMPurify from "dompurify";
import SpeacersFon from "../../img/speacers_fon.webp";

const ACCORDION_LAYOUTS = [
    "panel_discussion",
    "panel_сollective",
    "public_interview",
];

const getTheme = (field) => {
    switch (field.acf_fc_layout) {
        case "panel_discussion":
            return field.theme_panell;
        case "panel_сollective":
            return field.theme_сollective;
        case "public_interview":
            return field.theme_interview;
        case "single_speacer":
            return field.theme;
        case "coffee_break":
            return field.break_title;
        default:
            return null;
    }
};

const toArray = (value) =>
    Array.isArray(value) ? value : value ? [value] : [];

const SpeakerCard = ({
    speaker,
    speakerIndex,
    badge,
    className = "",
    inlinePosition = false,
}) => (
    <div
        className={`speaker border border-[#245864] p-4 rounded-lg min-w-0 ${className}`}
    >
        <div className="flex items-start gap-3 mb-2">
            <div className="speaker-foto relative w-20 shrink-0">
                {speaker.acf?.sm_image && (
                    <img
                        src={speaker.acf.sm_image}
                        alt={speaker.post_title || "Спікер"}
                        className="w-full h-full object-cover rounded-lg bg-gray-200/60"
                        style={{
                            backgroundImage: `url(${SpeacersFon})`,
                            backgroundSize: "cover",
                            backgroundPosition: "left top",
                        }}
                    />
                )}

                {badge && speakerIndex === 0 && (
                    <span className="block bg-black text-white text-center uppercase text-[9px] px-1 py-1 rounded-lg">
                        {badge}
                    </span>
                )}
            </div>

            <div className="speaker-info flex flex-col gap-2 min-w-0">
                <h4 className="text-[14px]">
                    {speaker.post_title}
                </h4>

                {inlinePosition && speaker.acf?.position && (
                    <p className="text-[12px] text-[#333]">
                        {speaker.acf.position}
                    </p>
                )}

                {speaker.acf?.logo && (
                    <img
                        src={speaker.acf.logo}
                        alt="Логотип компанії"
                        style={{
                            width: `${speaker.acf.logo_mw || 40}px`,
                        }}
                        className="max-w-full h-auto"
                    />
                )}
            </div>
        </div>

        {!inlinePosition && speaker.acf?.position && (
            <p className="text-[12px] text-[#333]">
                {speaker.acf.position}
            </p>
        )}
    </div>
);

const Thesis = ({ html, className = "" }) =>
    html ? (
        <div
            className={`thesises-panel thesis-list text-justify text-[14px] font-normal ${className}`}
            dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(html),
            }}
        />
    ) : null;

const ProgramContent = ({ field }) => {
    switch (field.acf_fc_layout) {
        case "panel_discussion":
        case "panel_сollective":
        case "public_interview": {
            const isCollective =
                field.acf_fc_layout === "panel_сollective";

            const isInterview =
                field.acf_fc_layout === "public_interview";

            const thesis = isCollective
                ? field.thesises_сollective
                : field.thesises_panell;

            const badge = isCollective
                ? null
                : isInterview
                ? "Інтерв'юер"
                : "Модератор";

            return (
                <div className="panel-discussion flex flex-col lg:flex-row-reverse gap-4 sm:py-4">
                    <Thesis
                        html={thesis}
                        className="lg:w-2/5 lg:shrink-0"
                    />

                    <div className="speakers flex-1 min-w-0 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {toArray(field.speacers).map(
                            (speaker, index) => (
                                <SpeakerCard
                                    key={index}
                                    speaker={speaker}
                                    speakerIndex={index}
                                    badge={badge}
                                />
                            )
                        )}
                    </div>
                </div>
            );
        }

        case "single_speacer":
            return (
                <div className="single-speacer flex flex-col lg:flex-row-reverse lg:items-start lg:justify-end gap-4 sm:py-4">
                    <Thesis
                        html={field.thesises}
                        className="lg:w-1/2"
                    />

                    <div className="flex flex-wrap gap-4 md:w-[30%]">
                        {toArray(field.speacer).map(
                            (speaker, index) => (
                                <SpeakerCard
                                    key={index}
                                    speaker={speaker}
                                    speakerIndex={index}
                                    inlinePosition
                                    className="w-full"
                                />
                            )
                        )}
                    </div>
                </div>
            );

        default:
            return null;
    }
};

const EventProgram = ({ programData = [] }) => {
    const [openPanel, setOpenPanel] = useState(null);
    const [isExpanded, setIsExpanded] = useState(false);

    useEffect(() => {
        for (let i = 0; i < programData.length; i++) {
            const fields =
                programData[i]?.acf?.program_fields || [];

            for (let j = 0; j < fields.length; j++) {
                if (
                    toArray(fields[j].select_field).some(
                        (field) =>
                            ACCORDION_LAYOUTS.includes(
                                field.acf_fc_layout
                            )
                    )
                ) {
                    setOpenPanel(`${i}-${j}`);
                    return;
                }
            }
        }

        setOpenPanel(null);
    }, [programData]);

    return (
        <section className="py-20" id="programm">
            <div className="container max-w-screen-xl mx-auto pb-8 px-4">
                {programData.length > 0 ? (
                    <>
                        <div
                            className={
                                isExpanded
                                    ? ""
                                    : "max-h-[930px] sm:max-h-[760px] overflow-hidden"
                            }
                        >
                            <h2 className="font-bold uppercase text-3xl text-[#245864] mb-6 sm:text-4xl lg:text-left lg:text-[60px]">
                                Програма 2025
                            </h2>

                            {programData.flatMap(
                                (item, index) =>
                                    toArray(
                                        item.acf?.program_fields
                                    ).map(
                                        (layoutData, subIndex) => {
                                            const { program_time } =
                                                layoutData;

                                            const selectFields =
                                                toArray(
                                                    layoutData.select_field
                                                );

                                            const isAccordion =
                                                selectFields.some(
                                                    (field) =>
                                                        ACCORDION_LAYOUTS.includes(
                                                            field.acf_fc_layout
                                                        )
                                                );

                                            const panelId = `${index}-${subIndex}`;
                                            const isOpen =
                                                openPanel === panelId;

                                            const header = (
                                                <>
                                                    <span className="flex items-center gap-[10px] w-[130px] shrink-0 text-black font-medium">
                                                        <RxCounterClockwiseClock className="text-[#002CBA]/60 shrink-0" />
                                                        {program_time}
                                                    </span>

                                                    <div className="flex-1 min-w-0">
                                                        {selectFields.map(
                                                            (
                                                                field,
                                                                fieldIndex
                                                            ) => {
                                                                const theme =
                                                                    getTheme(
                                                                        field
                                                                    );

                                                                return theme ? (
                                                                    <h3
                                                                        key={
                                                                            fieldIndex
                                                                        }
                                                                        className="sm:text-xl text-[18px] font-semibold leading-tight"
                                                                        dangerouslySetInnerHTML={{
                                                                            __html:
                                                                                DOMPurify.sanitize(
                                                                                    theme
                                                                                ),
                                                                        }}
                                                                    />
                                                                ) : null;
                                                            }
                                                        )}
                                                    </div>

                                                    {isAccordion && (
                                                        <span
                                                            aria-hidden="true"
                                                            className={`ml-auto shrink-0 transition-transform duration-300 ${
                                                                isOpen
                                                                    ? "rotate-180"
                                                                    : ""
                                                            }`}
                                                        >
                                                            <svg
                                                                width="18"
                                                                height="18"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                strokeWidth="2"
                                                            >
                                                                <path d="m6 9 6 6 6-6" />
                                                            </svg>
                                                        </span>
                                                    )}
                                                </>
                                            );

                                            return (
                                                <div
                                                    key={subIndex}
                                                    className={`program-item p-3 rounded-[8px] ${
                                                        subIndex % 2 === 0
                                                            ? "bg-[#95C8BF]/10"
                                                            : ""
                                                    }`}
                                                >
                                                    {isAccordion ? (
                                                        <button
                                                            type="button"
                                                            aria-expanded={
                                                                isOpen
                                                            }
                                                            onClick={() =>
                                                                setOpenPanel(
                                                                    isOpen
                                                                        ? null
                                                                        : panelId
                                                                )
                                                            }
                                                            className="program-header flex flex-col md:flex-row items-start md:items-center gap-[15px] w-full text-left"
                                                        >
                                                            {header}
                                                        </button>
                                                    ) : (
                                                        <div className="program-header flex flex-col md:flex-row items-start md:items-center gap-[15px]">
                                                            {header}
                                                        </div>
                                                    )}

                                                    {isAccordion ? (
                                                        <div
                                                            className={
                                                                isOpen
                                                                    ? "pt-4"
                                                                    : "hidden"
                                                            }
                                                        >
                                                            {selectFields.map(
                                                                (
                                                                    field,
                                                                    fieldIndex
                                                                ) => (
                                                                    <ProgramContent
                                                                        key={
                                                                            fieldIndex
                                                                        }
                                                                        field={
                                                                            field
                                                                        }
                                                                    />
                                                                )
                                                            )}
                                                        </div>
                                                    ) : (
                                                        selectFields.map(
                                                            (
                                                                field,
                                                                fieldIndex
                                                            ) => (
                                                                <ProgramContent
                                                                    key={
                                                                        fieldIndex
                                                                    }
                                                                    field={
                                                                        field
                                                                    }
                                                                />
                                                            )
                                                        )
                                                    )}
                                                </div>
                                            );
                                        }
                                    )
                            )}
                        </div>

                        <div className="text-center mt-10">
                            <button
                                type="button"
                                onClick={() => {
                                    setIsExpanded(
                                        (prev) => !prev
                                    );

                                    if (isExpanded)
                                        document
                                            .getElementById(
                                                "programm"
                                            )
                                            ?.scrollIntoView({
                                                behavior: "smooth",
                                            });
                                }}
                                className="inline-block bg-[#245864] text-white font-bold px-5 py-3 uppercase tracking-widest hover:scale-105 transition-transform duration-300"
                            >
                                {isExpanded
                                    ? "Сховати"
                                    : "Повна програма"}
                            </button>
                        </div>
                    </>
                ) : (
                    <div className="text-center py-8">
                        <p className="text-gray-500">
                            Програма завантажується...
                        </p>
                    </div>
                )}
            </div>
        </section>
    );
};

export default EventProgram;
